/* ==============================================================================
   Palco da chamada
   ==============================================================================
   Onde as telas compartilhadas e as câmeras aparecem. É a tela que o produto
   existe para mostrar.

   A ESTRUTURA, que é o que mudou
   ==============================
   Havia duas disposições alternativas e excludentes: ou uma fonte em foco com
   tira de miniaturas embaixo, ou uma grade de rostos. Trocar de uma para a outra
   reorganizava a tela inteira — a pessoa compartilhava a tela e todos os rostos
   pulavam de lugar.

   Agora há três faixas FIXAS, sempre nesta ordem, e o que muda é só a altura:

     1. CABEÇALHO   nome do canal + quantos estão dentro, e as telas
                    transmitidas como cartões no canto SUPERIOR DIREITO.
     2. FOCO        a fonte grande. Colapsa a zero quando não há nada em foco.
     3. GRADE       os participantes, cartões quadrados de 96px com o nome
                    ABAIXO do cartão.

   Os cartões de tela no cabeçalho funcionam como ABAS: clicar troca o que está
   na faixa de foco, e o cartão escolhido ganha o selo "Activo". É o que resolve
   o problema real de layout — numa chamada de 8 pessoas com 3 telas há 11 fontes
   de vídeo e uma área fixa; dar o mesmo tamanho a todas produz 11 miniaturas
   ilegíveis, e a tela com o código fica do tamanho de um selo.

   O foco é PEGAJOSO: uma tela nova compartilhada não rouba o foco de quem está
   olhando outra coisa. Roubar seria arrancar a pessoa do que ela escolheu ver.

   Nome ABAIXO do cartão, e não dentro
   ===================================
   Legenda por cima da imagem obriga um véu escuro atrás do texto para ele ser
   legível, e o véu come o canto do rosto. Fora do cartão o nome fica sobre a
   superfície do painel, com o contraste dos tokens de texto, e o cartão fica
   inteiro para a imagem.

   Por que `<video>` recebe o stream por ref e não por prop
   =======================================================
   `srcObject` é uma propriedade do elemento, não um atributo — React não a
   serializa. Passar `src={stream}` produz a string "[object MediaStream]". O ref
   é o único caminho, e é por isso que existe o `VideoSurface` abaixo em vez de um
   `<video>` inline.

   O `<audio>` NÃO mora mais aqui, e o motivo é um defeito real
   ===========================================================
   Havia um `AudioSink` neste arquivo, um `<audio>` por participante remoto, e ele
   NUNCA tocou som nenhum. Ele procurava a faixa de áudio dentro da lista
   `videos`, e essa lista é montada com um filtro que descarta exatamente
   `purpose === 'microphone'` — então `audioStream` era sempre `null`, o efeito
   saía cedo, e nenhum elemento recebia stream. O sintoma era o pior possível:
   uma chamada visualmente perfeita e completamente muda, sem erro no console.

   A reprodução passou para `apps/web/src/components/VoiceAudio.tsx`, fora do
   palco — e isso é melhoria e não só correção: o áudio precisa sobreviver à troca
   de canal e à desmontagem do palco, e um `<audio>` que vive dentro do componente
   de vídeo morre junto com ele. O palco ficou responsável só pelo que se vê.

   Consequência para quem lê este arquivo: NÃO reintroduza elementos de áudio
   aqui. Dois pontos de reprodução tocam a mesma voz duas vezes, com alguns
   milissegundos de diferença — o que não soa como volume dobrado, soa como
   flanging.
   ============================================================================== */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Avatar, type AvatarMedia, type AvatarStatus } from '../components/Avatar.tsx';
import {
  FloatingPanel,
  PopoverDivider,
  PopoverHeader,
  PopoverRow,
} from '../components/Popover.tsx';
import { VolumeSlider } from '../components/Controls.tsx';

/* ------------------------------------------------------------------------------
   Modelo
------------------------------------------------------------------------------ */

export interface StageParticipant {
  userId: string;
  sessionId: string;
  displayName: string;
  avatar?: AvatarMedia | null;
  status?: AvatarStatus;
  isSelf: boolean;
  muted: boolean;
  deafened: boolean;
  speaking: boolean;
  /** `true` quando a conexão oscila. Mostra aviso sem remover da lista. */
  unstable: boolean;
  connectionState: string;
  /**
   * Volume LOCAL desta fonte, 0 a 1. Ausente = 1.
   *
   * Mora no participante e não num mapa à parte de propósito: o palco lista
   * fontes de áudio, uma por sessão, e um `Record<sessionId, number>` paralelo
   * teria de ser mantido em sincronia com a lista a cada entrada e saída. Como
   * campo, ele nasce e morre com a sessão.
   *
   * O palco NÃO reproduz áudio — ver o cabeçalho do arquivo. Ele mostra o valor
   * e devolve o novo por `onSetVolume`; quem aplica é o consumidor.
   */
  volume?: number | undefined;
}

export interface StageVideo {
  /** Identificador único do fluxo. */
  id: string;
  stream: MediaStream;
  kind: 'screen' | 'camera';
  /** Rótulo humano: "Tela 1", "Câmera". */
  label: string;
  ownerSessionId: string;
  ownerName: string;
  /** `true` se é o vídeo local (espelhado, sem áudio). */
  /**
   * `true` para o que EU estou enviando.
   *
   * `VideoSurface` deriva o espelhamento daqui: `isLocal && kind === 'camera'`
   * espelha, todo o resto não. Não existe um campo `mirrored` de propósito — ele
   * seria uma segunda fonte de verdade para algo que já se sabe pelos dois
   * campos, e a única forma de os dois discordarem é um bug.
   */
  isLocal: boolean;
}

export interface VoiceStageProps {
  channelName: string;
  participants: StageParticipant[];
  videos: StageVideo[];
  /**
   * Segunda metade do título: `#geral — Reunião Semanal`.
   *
   * Existe porque o nome do canal sozinho não diz o que está acontecendo — um
   * canal chamado "geral" é permanente, a reunião não é. Opcional: sem valor, o
   * cabeçalho mostra só o canal, sem travessão órfão.
   */
  sessionLabel?: string | undefined;
  /**
   * Ajustar o volume de uma fonte, SÓ PARA MIM.
   *
   * Chaveado por `sessionId` e não por `userId`: a mesma pessoa pode estar na
   * chamada de dois dispositivos, e são duas fontes de áudio distintas — baixar
   * "o volume do João" baixaria as duas de uma vez, inclusive a que estava boa.
   *
   * Ausente = o controle não aparece.
   */
  onSetVolume?: ((sessionId: string, volume: number) => void) | undefined;
  /**
   * Parar uma tela que EU estou transmitindo.
   *
   * Recebe o `id` do `StageVideo`, que é o `sourceId` da captura. Só aparece em
   * cartão de tela local: parar a transmissão de outra pessoa não é uma ação de
   * interface, é moderação, e mora na lista de membros.
   *
   * O botão fica no cartão da tela e não na barra de comando porque com duas
   * telas no ar "parar de compartilhar" é ambíguo — qual delas? O cartão já
   * responde isso por posição.
   */
  onStopScreen?: ((sourceId: string) => void) | undefined;
  /** `true` quando o usuário ensurdeceu. Mostra a faixa de aviso no topo. */
  deafened?: boolean;
  /** Aviso do governador de banda ou da malha. */
  notice?: string | null;
  onDismissNotice?: () => void;
  /**
   * Menu de contexto PRÓPRIO do consumidor.
   *
   * Tem precedência sobre o popover interno: quem passa este callback quer o seu
   * menu, e dois menus abrindo no mesmo clique direito seria pior que nenhum. Sem
   * ele, e havendo `onSetVolume`, o palco abre o próprio painel com o slider.
   */
  onParticipantContextMenu?: (participant: StageParticipant, x: number, y: number) => void;
  /**
   * Clique num cartão de participante.
   *
   * O palco JÁ resolve o efeito visual do clique por conta — coloca a câmera da
   * pessoa na faixa de foco. Este callback é para o consumidor reagir ao lado
   * (abrir perfil) e é opcional justamente porque o comportamento principal não
   * depende dele.
   */
  onSelectParticipant?: ((participant: StageParticipant) => void) | undefined;

  /**
   * Entrar na chamada deste canal.
   *
   * Presente SÓ quando a pessoa está fora dela. É a saída de um estado que antes
   * não tinha nenhuma: a sala selecionada, gente dentro, e o cliente fora — sem
   * botão, porque clicar no canal já selecionado não muda rota e não faz nada.
   */
  onJoin?: (() => void) | undefined;

  /**
   * Sessões cuja transmissão está sem som.
   *
   * A informação vem de fora porque quem reproduz áudio é o consumidor — ver o
   * cabeçalho do arquivo. O palco só desenha o estado e devolve a intenção.
   */
  screenAudioMuted?: ReadonlySet<string> | undefined;
  onSetScreenAudioMuted?: ((sessionId: string, muted: boolean) => void) | undefined;
}

/* ==============================================================================
   Geometria
   ==============================================================================
   96 é o lado do cartão de participante e sai de uma restrição: com o nome FORA
   do cartão, ele precisa caber em uma linha sob 96px de largura sem truncar um
   primeiro nome típico. Abaixo de 88 quase todo nome trunca; acima de 112 caberia
   metade dos participantes numa grade de painel médio.
============================================================================== */
const TILE = 96;
/** Cartão de tela transmitida no cabeçalho. 16:9 em cima de 152px de largura. */
const SCREEN_CARD_W = 152;
const SCREEN_CARD_H = 86;

export function VoiceStage({
  channelName,
  participants,
  videos,
  sessionLabel,
  onSetVolume,
  onStopScreen,
  deafened = false,
  notice,
  onDismissNotice,
  onParticipantContextMenu,
  onSelectParticipant,
  onJoin,
  screenAudioMuted,
  onSetScreenAudioMuted,
}: VoiceStageProps) {
  /**
   * Foco pegajoso.
   *
   * `null` significa "escolha automática": a primeira tela, ou nada se não
   * houver tela. Depois de o usuário clicar, o id fica fixo — e uma tela nova
   * NÃO rouba o foco. Roubar arrancaria a pessoa do que ela escolheu ver, que é
   * o comportamento mais irritante possível durante uma apresentação.
   */
  const [focusedId, setFocusedId] = useState<string | null>(null);

  /* ---- Tela cheia ---------------------------------------------------------
     O elemento que entra em tela cheia e o CONTENTOR do video, nao o `<video>`.

     Com o `<video>` direto, o navegador usa os controles nativos dele e some com
     tudo que esta desenhado por cima: a etiqueta de quem transmite, o botao de
     parar, o proprio botao de sair da tela cheia. Em tela cheia de um `<video>`
     sem `controls`, a pessoa fica sem nenhuma saida visivel alem do Esc -- e o
     Esc nao e obvio para quem entrou clicando.

     Com o contentor, o overlay inteiro vai junto e continua clicavel.
  ------------------------------------------------------------------------- */
  const focusRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /*
   * O estado acompanha o EVENTO do navegador, nao o clique.
   *
   * Sair com Esc, com F11, ou porque outro elemento entrou em tela cheia nao
   * passa pelo nosso handler. Um booleano atualizado no clique ficaria mentindo
   * -- o botao diria "sair" com a pagina ja fora da tela cheia.
   */
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === focusRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const element = focusRef.current;
    if (element === null) return;

    if (document.fullscreenElement === element) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }

    /*
     * `catch` silencioso, e nao um aviso na tela.
     *
     * `requestFullscreen` rejeita quando a politica de permissao do documento
     * nega (um iframe sem `allow="fullscreen"`, por exemplo) ou quando a chamada
     * nao veio de um gesto do usuario. Nos dois casos a pessoa continua vendo o
     * video no tamanho normal, que e um degrade aceitavel -- e um erro sobre
     * "tela cheia" no meio de uma reuniao e ruido.
     */
    void element.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
  }, []);

  /*
   * Duplo clique no video tambem alterna, como em qualquer player.
   *
   * E o gesto que as pessoas ja tem no dedo. O botao existe porque duplo clique
   * nao e descobrivel e nao funciona por teclado.
   */
  const onFocusDoubleClick = toggleFullscreen;

  /* ==========================================================================
     Painel de volume: guarda o ID, não o objeto
     ==========================================================================
     Guardava `participant: StageParticipant` — o OBJETO capturado no instante
     do clique direito. E era a razão de "o botão de volume não age em tempo
     real": o áudio mudava na hora, mas o painel continuava desenhando o valor
     congelado, então o controle voltava sozinho para onde estava e o número
     não mexia. O som obedecia e a interface dizia que não.

     Guardando o `sessionId` e relendo de `participants` a cada render, o painel
     mostra o estado atual. De quebra, ele acompanha qualquer outra coisa que
     mude naquela pessoa enquanto o painel está aberto — ela mutar o microfone,
     a conexão dela ficar instável, ela sair da chamada.
  ========================================================================== */
  const [volumeMenu, setVolumeMenu] = useState<
    { sessionId: string; x: number; y: number } | null
  >(null);

  /*
   * Sai `null` quando a pessoa não está mais na lista.
   *
   * Alguém pode sair da chamada com o painel de volume aberto, e um painel
   * ajustando o volume de quem não está mais lá é um controle sem efeito — pior
   * ainda porque parece funcionar.
   */
  const volumeTarget = useMemo(
    () =>
      volumeMenu === null
        ? null
        : (participants.find((p) => p.sessionId === volumeMenu.sessionId) ?? null),
    [volumeMenu, participants],
  );

  const screens = useMemo(() => videos.filter((v) => v.kind === 'screen'), [videos]);

  const focused = useMemo(() => {
    if (focusedId !== null) {
      const match = videos.find((v) => v.id === focusedId);
      if (match) return match;
      // O foco apontava para um vídeo que terminou. Cai na escolha automática em
      // vez de mostrar vazio.
    }
    return screens[0] ?? null;
  }, [focusedId, videos, screens]);

  // Limpa o foco quando o vídeo focado desaparece, para o `useMemo` acima não
  // ficar recalculando um id morto em cada render.
  useEffect(() => {
    if (focusedId !== null && !videos.some((v) => v.id === focusedId)) {
      setFocusedId(null);
    }
  }, [focusedId, videos]);

  /**
   * Quem está transmitindo tela.
   *
   * A checagem é dupla porque as telas LOCAIS chegam com `ownerSessionId` que
   * não corresponde a nenhuma sessão da malha — quem publica não se vê como par.
   * Sem o segundo teste, o próprio usuário era o único que não recebia o selo de
   * "hosting", exatamente enquanto apresentava.
   */
  const hostingSessions = useMemo(() => {
    const set = new Set<string>();
    let selfHosting = false;
    for (const video of videos) {
      if (video.kind !== 'screen') continue;
      if (video.isLocal) selfHosting = true;
      set.add(video.ownerSessionId);
    }
    if (selfHosting) {
      const self = participants.find((p) => p.isSelf);
      if (self) set.add(self.sessionId);
    }
    return set;
  }, [videos, participants]);

  const handleSelect = useCallback(
    (participant: StageParticipant) => {
      const camera = videos.find(
        (v) => v.ownerSessionId === participant.sessionId && v.kind === 'camera',
      );
      if (camera) setFocusedId(camera.id);
      onSelectParticipant?.(participant);
    },
    [videos, onSelectParticipant],
  );

  /**
   * Clique direito: menu do consumidor se houver, senão o painel de volume.
   *
   * Devolve `undefined` quando não há nada para abrir, e é isso que o cartão usa
   * para NÃO registrar `onContextMenu` — assim o menu nativo do navegador
   * continua funcionando onde a interface não tem nada a oferecer, em vez de
   * engolir o clique direito e não fazer nada.
   */
  const contextHandler =
    onParticipantContextMenu !== undefined
      ? onParticipantContextMenu
      : onSetVolume !== undefined
        ? (participant: StageParticipant, x: number, y: number) => {
            setVolumeMenu({ sessionId: participant.sessionId, x, y });
          }
        : undefined;

  const connected = participants.length;

  return (
    <div className="flex h-full min-h-0 flex-col px-4 pb-2 pt-3" style={{ gap: 'var(--spacing-3)' }}>
      <style>{`
        /*
          O anel de quem fala é de ACENTO, não verde, e pulsa.
          --------------------------------------------------
          A referência pede violeta luminoso, e há um motivo funcional para o
          verde ter saído: verde no palco significava "falando" e verde na
          sidebar significa "online". Duas coisas diferentes com a mesma cor na
          mesma tela.

          Cor não carrega o estado sozinha — o selo de microfone no canto do
          cartão diz aberto ou mudo por FORMA, e é ele o canal acessível.
        */
        @keyframes stage-speaking {
          0%, 100% {
            box-shadow: 0 0 0 2px var(--color-accent),
                        0 0 14px var(--color-accent-glow);
          }
          50% {
            box-shadow: 0 0 0 3px var(--color-accent),
                        0 0 26px var(--color-accent-glow),
                        0 0 48px var(--color-accent-glow);
          }
        }
        .stage-speaking { animation: stage-speaking 1.5s var(--ease-in-out) infinite; }

        @media (prefers-reduced-motion: reduce) {
          /* Não desliga: reduz. O anel comunica ESTADO, e um estado que só
             existe em movimento desaparece para quem pediu menos movimento. */
          .stage-speaking { animation-duration: 3s !important; }
        }
      `}</style>

      {notice != null && notice.length > 0 && (
        <BandwidthNotice notice={notice} onDismiss={onDismissNotice} />
      )}

      {/* ----------------------------------------------------------------------
          Você não está ouvindo ninguém
          ----------------------------------------------------------------------
          Ensurdecer é o estado mais fácil de esquecer que se ativou: o botão fica
          na barra de baixo e o silêncio resultante é indistinguível de "ninguém
          está falando". A faixa fica no palco, que é para onde a pessoa olha
          quando estranha o silêncio.
      ---------------------------------------------------------------------- */}
      {deafened && (
        <div
          role="status"
          className="flex shrink-0 items-center px-3 py-1.5"
          style={{
            gap: 'var(--spacing-2)',
            borderRadius: 'var(--radius-full)',
            alignSelf: 'flex-start',
            background: 'var(--color-danger-subtle)',
            fontSize: 'var(--text-2xs)',
            color: 'var(--color-danger-on-subtle)',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3.4 10V8a4.6 4.6 0 019.2 0v2" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" />
            <path d="M2.6 2.6l10.8 10.8" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" />
          </svg>
          Áudio silenciado — você não está ouvindo a chamada
        </div>
      )}

      {/* ======================================================================
          FAIXA 1 — cabeçalho
      ====================================================================== */}
      <header className="flex shrink-0 flex-wrap items-start justify-between"
              style={{ gap: 'var(--spacing-4)' }}>
        <div className="min-w-0">
          <h2 className="flex min-w-0 flex-wrap items-baseline"
              style={{ gap: 'var(--spacing-2)' }}>
            {/* O `#` é mais fino e apagado que o nome: é sintaxe, não conteúdo.
                No mesmo peso do nome ele lê como a primeira letra dele. */}
            <span className="truncate font-bold"
                  style={{
                    fontSize: 'var(--text-xl)',
                    letterSpacing: 'var(--tracking-tight)',
                    color: 'var(--color-text-primary)',
                  }}>
              <span aria-hidden="true"
                    style={{ color: 'var(--color-text-disabled)', fontWeight: 400 }}>
                #
              </span>
              {channelName}
            </span>
            {sessionLabel !== undefined && sessionLabel.length > 0 && (
              <span className="truncate"
                    style={{
                      fontSize: 'var(--text-md)',
                      color: 'var(--color-text-tertiary)',
                      letterSpacing: 'var(--tracking-tight)',
                    }}>
                — {sessionLabel}
              </span>
            )}
          </h2>
          <p className="mt-0.5 flex items-center"
             style={{
               gap: 'var(--spacing-2)',
               fontSize: 'var(--text-xs)',
               color: 'var(--color-text-tertiary)',
             }}>
            <span aria-hidden="true" className="block size-1.5 rounded-full"
                  style={{
                    background: 'var(--color-success)',
                    boxShadow: '0 0 10px var(--color-success)',
                  }} />
            {connected === 1
              ? '1 participante conectado'
              : `${connected} participantes conectados`}
          </p>
        </div>

        {/* --------------------------------------------------------------------
            Entrar, quando você está de fora
            --------------------------------------------------------------------
            Aparece só com `onJoin`, e o consumidor só o passa quando a pessoa
            NÃO está nesta chamada.

            Ele existe por causa de um beco sem saída real: a sala selecionada,
            gente dentro dela, e o cliente fora — porque a entrada falhou ou a
            malha caiu. Clicar no canal de novo não resolvia, já que ele já
            estava selecionado e o clique não mudava rota. A tela mostrava a
            sala e não oferecia como entrar nela.
        -------------------------------------------------------------------- */}
        {onJoin !== undefined && (
          <button
            type="button"
            onClick={onJoin}
            className="flex shrink-0 items-center gap-2 px-4 py-2 font-semibold transition-colors"
            style={{
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-xs)',
              boxShadow: 'var(--glow-soft)',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"
                 stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                 strokeLinejoin="round">
              <path d="M2.2 4.2c3.6-2.4 8-2.4 11.6 0l-1.6 2-2.3-.7v-1.3c-1.3-.4-2.6-.4-3.8 0v1.3l-2.3.7-1.6-2z" />
            </svg>
            Entrar na chamada
          </button>
        )}

        {/* --------------------------------------------------------------------
            Telas transmitidas — cartões em LINHA, no canto superior direito
            --------------------------------------------------------------------
            Duas lado a lado quando há duas. Além de três a linha rola: um quarto
            cartão de 152px empurraria o título, e o título é o que ancora a
            leitura da tela.
        -------------------------------------------------------------------- */}
        {screens.length > 0 && (
          <div className="flex shrink-0 items-start"
               style={{ gap: 'var(--spacing-2)', maxWidth: '100%' }}>
            {screens.map((screen) => (
              <ScreenCard
                key={screen.id}
                video={screen}
                active={focused?.id === screen.id}
                onClick={() => setFocusedId(screen.id)}
                {...(screen.isLocal && onStopScreen
                  ? { onStop: () => onStopScreen(screen.id) }
                  : {})}
              />
            ))}
          </div>
        )}
      </header>

      {/* ======================================================================
          FAIXA 2 — foco
          ======================================================================
          `flex: 1` só quando há algo em foco. Sem isso, um contêiner vazio
          roubaria metade da altura do palco quando a chamada é só de áudio.
      ====================================================================== */}
      {focused !== null && (
        <div
          ref={focusRef}
          onDoubleClick={onFocusDoubleClick}
          /* ----------------------------------------------------------------
             Botão direito NA TRANSMISSÃO
             ----------------------------------------------------------------
             Faltava, e o pedido foi exatamente este. O menu de volume existia
             só no cartão do participante, na grade lá embaixo — e quem está
             assistindo a uma transmissão está olhando para o vídeo, não para a
             fileira de avatares. Em tela cheia a grade nem existe, então não
             havia caminho nenhum para o volume.

             O alvo é o DONO do vídeo em foco, e não o vídeo: o que se ajusta é
             o volume de uma pessoa. Numa tela sem áudio o menu ainda faz
             sentido — a voz dela continua vindo pelo microfone.

             `preventDefault` só quando há menu a mostrar. Sem `onSetVolume` o
             manipulador não é registrado, e o menu nativo do navegador
             continua funcionando — que é o certo quando não há ação própria a
             oferecer.
          ---------------------------------------------------------------- */
          {...(onSetVolume !== undefined
            ? {
                onContextMenu: (event: ReactMouseEvent<HTMLDivElement>) => {
                  const dono = participants.find(
                    (p) => p.sessionId === focused.ownerSessionId,
                  );
                  if (!dono || dono.isSelf) return;
                  event.preventDefault();
                  setVolumeMenu({
                    sessionId: dono.sessionId,
                    x: event.clientX,
                    y: event.clientY,
                  });
                },
              }
            : {})}
          className="relative min-h-0 flex-1 overflow-hidden"
          style={{
            /*
             * Em tela cheia, o raio e a sombra SAEM.
             *
             * Canto arredondado contra a borda fisica do monitor deixa quatro
             * triangulos preto-sobre-preto que parecem defeito de renderizacao.
             * E `border-radius` num elemento em tela cheia e ignorado por alguns
             * navegadores e respeitado por outros -- resultado inconsistente.
             */
            borderRadius: isFullscreen ? 0 : 'var(--radius-xl)',
            /*
             * Fundo PRETO e não a superfície do tema.
             *
             * Vídeo com letterbox sobre cinza-azulado mostra as barras como
             * "área vazia da interface"; sobre preto elas leem como parte da
             * imagem. É a mesma razão pela qual um player de vídeo usa preto.
             */
            background: 'oklch(6% 0 0)',
            boxShadow: isFullscreen
              ? 'none'
              : 'var(--glass-edge), 0 10px 40px oklch(0% 0 0 / 0.45), var(--glow-soft)',
          }}
        >
          <VideoSurface video={focused} objectFit="contain" />

          {/* ----------------------------------------------------------------
              Quem está na sala, durante a tela cheia
              ----------------------------------------------------------------
              Coluna de avatares no canto ESQUERDO, translúcida, em que quem
              fala ganha opacidade.

              Por que só na tela cheia: fora dela a grade de participantes está
              logo abaixo, e repetir as mesmas pessoas em dois lugares na mesma
              tela é ruído. Na tela cheia a grade sai de vista, e aí a pergunta
              "quem está aqui e quem está falando" deixa de ter resposta.

              Por que dentro do contentor do foco: é ele que entra em tela cheia.
              Um irmão do contentor simplesmente não seria renderizado — a tela
              cheia mostra a subárvore do elemento e nada fora dela.
          ---------------------------------------------------------------- */}
          {isFullscreen && participants.length > 0 && (
            <div
              className="absolute left-3 flex flex-col"
              style={{
                /*
                 * Centralizado na vertical, não colado no topo.
                 *
                 * No topo a coluna disputa espaço com a etiqueta de quem
                 * transmite; embaixo, com a barra de tarefas do sistema em
                 * telas de 16:9. O meio é o único lugar que não colide com
                 * nada, e é onde o olho não está (o centro da imagem).
                 */
                top: '50%',
                transform: 'translateY(-50%)',
                gap: 'var(--spacing-2)',
                /*
                 * `pointer-events: none` na coluna inteira.
                 *
                 * Ela é informação, não controle. Sem isto, um clique perto da
                 * borda esquerda para dar duplo clique e sair da tela cheia
                 * cairia num avatar e não faria nada — e a pessoa concluiria que
                 * o duplo clique parou de funcionar.
                 */
                pointerEvents: 'none',
                /* Acima do vídeo, abaixo de qualquer popover do palco. */
                zIndex: 1,
              }}
            >
              {participants.map((participant) => (
                <FullscreenRosterItem key={participant.sessionId} participant={participant} />
              ))}
            </div>
          )}

          {/* ----------------------------------------------------------------
              Tela cheia
              ----------------------------------------------------------------
              No canto SUPERIOR DIREITO, oposto a etiqueta de quem transmite.
              Sobrepor os dois faria o botao cobrir o nome exatamente quando ele
              e mais consultado.
          ---------------------------------------------------------------- */}
          <button
            type="button"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Sair da tela cheia (Esc)' : 'Ver em tela cheia'}
            aria-label={isFullscreen ? 'Sair da tela cheia' : 'Ver em tela cheia'}
            aria-pressed={isFullscreen}
            className="absolute right-3 top-3 grid place-items-center"
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-md)',
              background: 'oklch(0% 0 0 / 0.58)',
              backdropFilter: 'blur(10px) saturate(160%)',
              WebkitBackdropFilter: 'blur(10px) saturate(160%)',
              boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.14)',
              /*
               * Branco quase puro, e nao um token de texto.
               *
               * Este botao fica sobre VIDEO, cujo conteudo e desconhecido. Um
               * token do tema mede contraste contra a superficie do tema, que
               * nao e o que esta atras aqui. Branco sobre a pastilha preta a 58%
               * de opacidade e o unico par que se pode garantir sem saber a cor
               * do frame -- a pastilha e que cria o fundo conhecido.
               */
              color: 'oklch(98% 0 0)',
            }}
          >
            {isFullscreen ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <path d="M9 3.5H4.5V8M15 3.5h4.5V8M9 20.5H4.5V16M15 20.5h4.5V16" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <path d="M3.5 9V3.5H9M20.5 9V3.5H15M3.5 15v5.5H9M20.5 15v5.5H15" />
              </svg>
            )}
          </button>

          <div
            className="absolute left-3 top-3 flex items-center px-2.5 py-1"
            style={{
              gap: 'var(--spacing-2)',
              borderRadius: 'var(--radius-full)',
              background: 'oklch(0% 0 0 / 0.58)',
              backdropFilter: 'blur(10px) saturate(160%)',
              WebkitBackdropFilter: 'blur(10px) saturate(160%)',
              boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.14)',
              fontSize: 'var(--text-2xs)',
              color: 'oklch(98% 0 0)',
            }}
          >
            <span aria-hidden="true" className="block size-1.5 rounded-full"
                  style={{
                    background: 'var(--color-danger)',
                    boxShadow: '0 0 8px var(--color-danger)',
                  }} />
            {focused.ownerName} · {focused.label}
          </div>
        </div>
      )}

      {/* ======================================================================
          FAIXA 3 — grade de participantes
      ====================================================================== */}
      <div
        className={`scroll-hover-only min-h-0 ${focused !== null ? 'shrink-0' : 'flex-1'} overflow-y-auto`}
        style={{
          /* Com algo em foco, a grade é uma tira de uma ou duas fileiras; sem
             foco, ela ocupa o palco. O teto em `vh` evita que 30 participantes
             empurrem a faixa de foco para fora da tela. */
          maxHeight: focused !== null ? '30vh' : undefined,
          paddingBottom: 'var(--spacing-1)',
        }}
      >
        <div
          className="grid justify-center"
          style={{
            /*
             * `auto-fill` com largura FIXA de cartão, não `minmax(…, 1fr)`.
             *
             * O cartão é quadrado e a foto o preenche: esticar a coluna
             * deformaria a proporção ou obrigaria a recortar mais o rosto. Com
             * largura fixa a grade só muda a CONTAGEM de colunas, e o cartão
             * continua sendo o mesmo objeto em qualquer largura de painel —
             * inclusive neste layout, em que o painel é redimensionável.
             */
            gridTemplateColumns: `repeat(auto-fill, ${TILE}px)`,
            gap: 'var(--spacing-4)',
          }}
        >
          {participants.map((participant) => {
            const camera = videos.find(
              (v) => v.ownerSessionId === participant.sessionId && v.kind === 'camera',
            );
            return (
              <ParticipantTile
                key={participant.sessionId}
                participant={participant}
                {...(camera ? { video: camera } : {})}
                hosting={hostingSessions.has(participant.sessionId)}
                focusedVideoId={focused?.id ?? null}
                muffled={
                  !participant.isSelf && (participant.volume ?? 1) === 0
                }
                onClick={() => handleSelect(participant)}
                {...(contextHandler ? { onContextMenu: contextHandler } : {})}
              />
            );
          })}

          {participants.length === 0 && (
            <div className="col-span-full grid place-items-center" style={{ minHeight: 160 }}>
              <p className="text-center"
                 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                Conectando a {channelName}…
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ======================================================================
          Painel de volume por pessoa
          ======================================================================
          Fora da grade e posicionado em coordenadas de viewport: um popover
          dentro do cartão seria recortado pela área de rolagem da grade, que é
          justamente onde ele precisa aparecer.
      ====================================================================== */}
      {volumeMenu !== null && volumeTarget !== null && onSetVolume !== undefined && (
        <FloatingPanel
          x={volumeMenu.x}
          y={volumeMenu.y}
          width={252}
          role="dialog"
          label={`Volume de ${volumeTarget.displayName}`}
          onClose={() => setVolumeMenu(null)}
        >
          <PopoverHeader
            title={volumeTarget.displayName}
            subtitle={
              volumeTarget.muted
                ? 'Microfone desativado'
                : volumeTarget.unstable
                  ? 'Conexão instável'
                  : 'Na chamada'
            }
            leading={
              <Avatar
                media={volumeTarget.avatar ?? null}
                name={volumeTarget.displayName}
                seed={volumeTarget.userId}
                size={28}
              />
            }
          />
          <PopoverDivider />
          <VolumeSlider
            name={volumeTarget.displayName}
            volume={volumeTarget.volume ?? 1}
            onChange={(next) => onSetVolume(volumeTarget.sessionId, next)}
          />

          {/* ------------------------------------------------------------
              Som da transmissão desta pessoa
              ------------------------------------------------------------
              Só aparece se ela está transmitindo alguma coisa. Um interruptor
              para o som de uma tela que não existe é um controle que não faz
              nada — e a pessoa que o aperta conclui que ele está quebrado, não
              que não havia o que desligar.

              Separado do volume acima de propósito: baixar o volume atinge a
              voz E a tela, porque são a mesma fonte para quem escuta. Este
              atinge só a tela, que é o pedido de quem quer ver o jogo sem
              ouvi-lo e continuar conversando.
          ------------------------------------------------------------ */}
          {onSetScreenAudioMuted !== undefined &&
            videos.some(
              (v) => v.ownerSessionId === volumeTarget.sessionId && !v.isLocal,
            ) && (
              <>
                <PopoverDivider />
                <PopoverRow
                  title={
                    screenAudioMuted?.has(volumeTarget.sessionId) === true
                      ? 'Ativar som da transmissão'
                      : 'Desativar som da transmissão'
                  }
                  subtitle={
                    screenAudioMuted?.has(volumeTarget.sessionId) === true
                      ? 'A voz continua; o som da tela volta a tocar.'
                      : 'A voz continua; só o som da tela é silenciado.'
                  }
                  onClick={() =>
                    onSetScreenAudioMuted(
                      volumeTarget.sessionId,
                      screenAudioMuted?.has(volumeTarget.sessionId) !== true,
                    )
                  }
                />
              </>
            )}

          {onSelectParticipant !== undefined && (
            <>
              <PopoverDivider />
              <PopoverRow
                title="Ver perfil"
                chevron
                onClick={() => {
                  onSelectParticipant(volumeTarget);
                  setVolumeMenu(null);
                }}
              />
            </>
          )}
        </FloatingPanel>
      )}
    </div>
  );
}

/* ==============================================================================
   Item da fila de participantes na tela cheia
   ==============================================================================
   Avatar pequeno e translúcido que fica opaco quando a pessoa fala.

   A opacidade É a informação, e é por isso que a transição importa
   ==============================================================================
   `0.35` calado e `1` falando. A diferença tem de ser grande o suficiente para
   ser lida de relance no canto do olho, sobre um vídeo de conteúdo desconhecido
   — que é o pior fundo possível para qualquer indicador.

   A transição é ASSIMÉTRICA de propósito: 80 ms para acender e 400 ms para
   apagar. Fala tem micro-pausas, e uma saída rápida faria a coluna inteira
   piscar durante uma frase normal. Acender rápido faz o indicador parecer
   instantâneo; apagar devagar faz ele parecer estável. O `SpeakingDetector` já
   aplica histerese de 250 ms no dado; isto é a mesma ideia no pixel.
============================================================================== */

function FullscreenRosterItem({ participant }: { participant: StageParticipant }) {
  const speaking = participant.speaking === true;

  return (
    <div
      className="flex items-center"
      style={{
        gap: 'var(--spacing-2)',
        opacity: speaking ? 1 : 0.35,
        /*
         * `opacity` e `transform` são as duas propriedades que o compositor
         * anima sem recalcular layout. Numa chamada com vídeo em tela cheia, uma
         * transição que force layout compete com a decodificação de quadros.
         */
        transform: speaking ? 'translateX(0)' : 'translateX(-2px)',
        transition: speaking
          ? 'opacity var(--duration-fast) ease-out, transform var(--duration-fast) ease-out'
          : 'opacity 400ms ease-in, transform 400ms ease-in',
      }}
    >
      <div
        className="relative shrink-0"
        style={{
          borderRadius: 'var(--radius-full)',
          /*
           * O anel roxo aparece só falando, e é o MESMO token do anel do card.
           *
           * Usar outra cor aqui faria "falando" ter dois vocabulários visuais no
           * mesmo produto. `box-shadow` e não `border`, pela razão de sempre:
           * borda muda o tamanho e a coluna saltaria a cada palavra.
           */
          boxShadow: speaking
            ? '0 0 0 2px var(--color-voice-speaking), 0 0 12px var(--color-voice-speaking)'
            : 'none',
          transition: 'box-shadow var(--duration-fast)',
        }}
      >
        <Avatar
          media={participant.avatar ?? null}
          name={participant.displayName}
          /*
           * `seed` é obrigatório e é o `userId`, não o nome.
           *
           * Ele alimenta o gradiente de fallback quando não há imagem. Semeando
           * pelo NOME, duas pessoas homônimas ficariam com a mesma cor — e o
           * gradiente existe justamente para diferenciar quem não tem avatar.
           * Semeando pelo id, a cor é estável entre sessões e única por pessoa.
           */
          seed={participant.userId}
          size={32}
        />
      </div>

      {/*
        O nome aparece SÓ de quem fala.
        ==============================
        Sete nomes empilhados sobre o vídeo cobrem uma faixa vertical inteira da
        imagem — e a pergunta que a coluna responde não é "quem está na sala"
        (isso o avatar já diz), é "quem está falando agora". O nome só é útil no
        instante em que alguém fala, e é quando ele aparece.
      */}
      {speaking && (
        <span
          className="max-w-[140px] truncate px-2 py-0.5"
          style={{
            borderRadius: 'var(--radius-full)',
            background: 'oklch(0% 0 0 / 0.58)',
            backdropFilter: 'blur(10px) saturate(160%)',
            WebkitBackdropFilter: 'blur(10px) saturate(160%)',
            boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.14)',
            fontSize: 'var(--text-2xs)',
            fontWeight: 600,
            /*
             * Branco quase puro sobre a pastilha preta, e não um token do tema.
             *
             * O fundo real é VÍDEO, de conteúdo desconhecido. Um token mede
             * contraste contra a superfície do tema, que não é o que está atrás.
             * A pastilha a 58% é o que cria um fundo conhecido para medir contra.
             */
            color: 'oklch(98% 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {participant.displayName}
        </span>
      )}
    </div>
  );
}

/* ==============================================================================
   Cartão de tela transmitida
   ==============================================================================
   Rótulo em caixa alta espaçada ACIMA do vídeo e selo de estado no canto. É um
   objeto de interface (uma aba), não um pedaço de conteúdo — e por isso tem
   moldura de painel em vez de ser um retângulo de vídeo solto.
============================================================================== */

function ScreenCard({
  video,
  active,
  onClick,
  onStop,
}: {
  video: StageVideo;
  active: boolean;
  onClick: () => void;
  onStop?: (() => void) | undefined;
}) {
  const [hover, setHover] = useState(false);

  return (
    /*
     * `div` com `role="group"` e um `button` interno para a área de vídeo, em vez
     * de um `<button>` envolvendo tudo.
     *
     * Botão dentro de botão é HTML inválido e o comportamento é imprevisível
     * entre navegadores — o clique em "parar" às vezes chega ao botão de fora e
     * troca o foco em vez de parar a transmissão. É o tipo de defeito que aparece
     * só na hora errada, com a tela de alguém no ar.
     */
    <div
      role="group"
      aria-label={`${video.ownerName} — ${video.label}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative shrink-0 overflow-hidden transition-transform"
      style={{
        width: SCREEN_CARD_W,
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface-overlay)',
        transform: hover && !active ? 'translateY(-2px)' : 'none',
        boxShadow: active
          ? 'var(--ring-accent), 0 8px 24px oklch(0% 0 0 / 0.40)'
          : 'var(--glass-edge), 0 4px 16px oklch(0% 0 0 / 0.35)',
        transitionDuration: 'var(--duration-fast)',
        transitionTimingFunction: 'var(--ease-out-quart)',
      }}
    >
      <div className="flex items-center justify-between px-2 pt-1.5">
        <span className="truncate font-semibold uppercase"
              style={{
                fontSize: 9,
                letterSpacing: 'var(--tracking-wider)',
                color: active
                  ? 'var(--color-accent-on-subtle)'
                  : 'var(--color-text-tertiary)',
              }}>
          Transmitindo tela
        </span>
        <span
          className="ml-1 flex shrink-0 items-center font-semibold uppercase"
          style={{
            gap: 3,
            padding: '1px 5px',
            borderRadius: 'var(--radius-full)',
            background: active
              ? 'var(--color-accent-subtle)'
              : 'var(--color-success-subtle)',
            color: active
              ? 'var(--color-accent-on-subtle)'
              : 'var(--color-success)',
            fontSize: 8,
            letterSpacing: 'var(--tracking-wide)',
          }}
        >
          <span aria-hidden="true" className="block size-1 rounded-full"
                style={{ background: 'currentColor' }} />
          {active ? 'Activo' : 'No ar'}
        </span>
      </div>

      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        title={
          active
            ? `${video.ownerName} · ${video.label} — em foco`
            : `Ver ${video.ownerName} · ${video.label} em tela grande`
        }
        className="relative mt-1 block w-full overflow-hidden"
        style={{ height: SCREEN_CARD_H, background: 'oklch(6% 0 0)' }}
      >
        <VideoSurface video={video} objectFit="cover" />
        <span className="sr-only">
          {active ? 'Em foco: ' : 'Colocar em foco: '}
          {video.ownerName} — {video.label}
        </span>
      </button>

      <div className="flex items-center justify-between px-2 py-1"
           style={{ gap: 'var(--spacing-1)' }}>
        <span className="min-w-0 truncate"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-secondary)' }}>
          {video.isLocal ? 'Você' : video.ownerName}
        </span>

        {/* Parar aparece SÓ na tela local, e com rótulo de texto e não um "×".
            Um "×" num cartão lê como "fechar a miniatura", e a consequência real
            é interromper a transmissão para todos na sala. */}
        {onStop && (
          <button
            type="button"
            onClick={onStop}
            className="shrink-0 font-semibold uppercase transition-colors"
            style={{
              padding: '1px 6px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-danger-subtle)',
              color: 'var(--color-danger-on-subtle)',
              fontSize: 9,
              letterSpacing: 'var(--tracking-wide)',
            }}
            title={`Parar de transmitir ${video.label}`}
          >
            Parar
          </button>
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Cartão de participante
============================================================================== */

function ParticipantTile({
  participant,
  video,
  hosting,
  focusedVideoId,
  muffled,
  onClick,
  onContextMenu,
}: {
  participant: StageParticipant;
  video?: StageVideo | undefined;
  hosting: boolean;
  focusedVideoId: string | null;
  /** Volume LOCAL em zero: silenciado só para mim, não para a sala. */
  muffled: boolean;
  onClick: () => void;
  onContextMenu?:
    | ((participant: StageParticipant, x: number, y: number) => void)
    | undefined;
}) {
  const [hover, setHover] = useState(false);
  const isFocused = video !== undefined && video.id === focusedVideoId;

  return (
    <div className="flex flex-col items-center" style={{ width: TILE }}>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onContextMenu={
          onContextMenu
            ? (event) => {
                event.preventDefault();
                onContextMenu(participant, event.clientX, event.clientY);
              }
            : undefined
        }
        className={`relative grid shrink-0 place-items-center overflow-hidden transition-[transform,box-shadow] ${
          participant.speaking ? 'stage-speaking' : ''
        }`}
        style={{
          width: TILE,
          height: TILE,
          borderRadius: 'var(--radius-xl)',
          background: video ? 'oklch(6% 0 0)' : 'var(--color-surface-overlay)',
          transform: hover ? 'translateY(-2px)' : 'none',
          /*
           * `box-shadow` e nunca `border` para o anel.
           *
           * Borda muda a caixa do cartão, e numa grade isso a faz SALTAR a cada
           * palavra dita — o cartão engorda 2px, os vizinhos se deslocam, e a
           * fileira toda tremula. A sombra é desenhada fora do box e não afeta
           * layout nenhum.
           *
           * A classe `.stage-speaking` sobrescreve isto quando a pessoa fala:
           * animação em classe ganha de `box-shadow` inline durante os
           * keyframes, e é por isso que o estado de fala não está nesta cadeia.
           */
          boxShadow: participant.unstable
            ? '0 0 0 2px var(--color-voice-weak), 0 4px 16px oklch(0% 0 0 / 0.35)'
            : isFocused
              ? 'var(--ring-accent)'
              : hover
                ? 'var(--glass-edge), 0 8px 22px oklch(0% 0 0 / 0.45)'
                : 'var(--glass-edge), 0 3px 12px oklch(0% 0 0 / 0.30)',
          transitionDuration: 'var(--duration-fast)',
          transitionTimingFunction: 'var(--ease-out-quart)',
          /* O anel do ponto de status do avatar tem de imitar o fundo do cartão,
             senão fica um halo cinza em volta dele. Ver Avatar.tsx. */
          ['--color-avatar-ring' as string]: video
            ? 'oklch(6% 0 0)'
            : 'var(--color-surface-overlay)',
        }}
      >
        {video ? (
          <VideoSurface video={video} objectFit="cover" />
        ) : (
          <Avatar
            media={participant.avatar ?? null}
            name={participant.displayName}
            seed={participant.userId}
            size={48}
            {...(participant.status !== undefined ? { status: participant.status } : {})}
          />
        )}

        {/* ------------------------------------------------------------------
            Selo de microfone — círculo no canto inferior direito
            ------------------------------------------------------------------
            Sempre presente, nos dois estados. Um selo que aparece só quando
            mudo obriga a interpretar a AUSÊNCIA de um elemento, e ausência é
            ambígua: "não tem selo" e "ainda não carregou" parecem iguais.
        ------------------------------------------------------------------ */}
        <span
          aria-hidden="true"
          className="absolute grid place-items-center"
          style={{
            right: 5,
            bottom: 5,
            width: 22,
            height: 22,
            borderRadius: 'var(--radius-full)',
            background: participant.muted
              ? 'var(--color-danger-solid)'
              : 'var(--color-accent-solid)',
            color: 'var(--color-on-solid)',
            /*
             * O anel de `--color-border-strong` não é decoração: é o que faz a
             * silhueta do selo passar 1.4.11.
             *
             * Medido: `--color-danger-solid` sobre o cartão de avatar
             * (`--color-surface-overlay`) dá 2.97:1 no tema escuro — reprova o
             * 3:1 de componente por três centésimos. O de acento passa (3.4:1),
             * e um selo que atende a norma quando o microfone está aberto e não
             * atende quando está mudo é o pior dos dois mundos: o estado que
             * precisa ser notado é justamente o mudo.
             *
             * Clarear `--color-danger-solid` não serve — ele carrega o glifo
             * claro e precisa continuar escuro o bastante para os 4.5:1. Um anel
             * resolve sem tocar no fill.
             *
             * O anel segue o CARTÃO e não o tema, e isso foi medido: no cartão
             * de avatar `--color-border-strong` é o token certo, porque ele é
             * calibrado para 3:1 sobre toda a escala de superfície nos três temas
             * (ver a nota dele em theme.css). No cartão com VÍDEO não serve — o
             * fundo ali é `oklch(6% 0 0)` sempre, inclusive no tema claro, e no
             * claro `border-strong` é PRETO a 45%: preto sobre quase-preto mede
             * 1.00:1, ou seja, o anel deixa de existir exatamente onde ele foi
             * posto para existir. Branco a 55% sobre o mesmo fundo dá 6.26:1.
             *
             * A lição geral: token de borda translúcida acompanha a inversão do
             * tema, e uma superfície que NÃO inverte (vídeo é preto nos dois)
             * precisa de valor próprio.
             */
            boxShadow:
              `0 0 0 1px ${
                video ? 'oklch(100% 0 0 / 0.55)' : 'var(--color-border-strong)'
              }, inset 0 1px 0 oklch(100% 0 0 / 0.20), 0 2px 6px oklch(0% 0 0 / 0.45)`,
          }}
        >
          <MicBadgeIcon muted={participant.muted} />
        </span>

        {/* Canto de baixo à esquerda: dois estados diferentes e nunca os dois
            ao mesmo tempo. `deafened` é escolha DELA (não ouve ninguém);
            `muffled` é escolha MINHA (eu não ouço ela). Confundir os dois é o
            erro que o rótulo do slider existe para evitar. */}
        {(participant.deafened || muffled) && (
          <span
            className="absolute grid place-items-center"
            style={{
              left: 5,
              bottom: 5,
              width: 18,
              height: 18,
              borderRadius: 'var(--radius-full)',
              background: participant.deafened
                ? 'var(--color-danger-solid)'
                : 'var(--color-surface-active)',
              color: participant.deafened
                ? 'var(--color-on-solid)'
                : 'var(--color-text-secondary)',
            }}
            title={
              participant.deafened
                ? 'Esta pessoa silenciou o áudio dela'
                : 'Você silenciou esta pessoa só para você'
            }
          >
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 3l10 10" stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" />
              <path d="M3.4 10V8a4.6 4.6 0 019.2 0v2" stroke="currentColor"
                    strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </span>
        )}

        <span className="sr-only">
          {participant.displayName}
          {participant.isSelf ? ' (você)' : ''}
          {participant.muted ? ', microfone desativado' : ', microfone aberto'}
          {participant.speaking ? ', falando' : ''}
          {hosting ? ', transmitindo tela' : ''}
          {muffled ? ', silenciada só para você' : ''}
          {participant.unstable ? ', conexão instável' : ''}
        </span>
      </button>

      {/* --------------------------------------------------------------------
          Nome ABAIXO do cartão
      -------------------------------------------------------------------- */}
      <span className="mt-1.5 block w-full truncate text-center"
            style={{
              fontSize: 'var(--text-2xs)',
              fontWeight: participant.speaking ? 600 : 500,
              color: participant.speaking
                ? 'var(--color-text-primary)'
                : 'var(--color-text-secondary)',
            }}>
        {participant.isSelf ? 'Você' : participant.displayName}
      </span>

      {/* "(hosting)" em texto pequeno, sem pastilha: é uma anotação sobre o
          nome, e uma pastilha colorida aqui competiria com o selo do microfone
          a 6px de distância. */}
      {hosting && (
        <span className="block w-full truncate text-center"
              style={{
                fontSize: 9,
                color: 'var(--color-accent-on-subtle)',
                letterSpacing: 'var(--tracking-wide)',
              }}>
          (hosting)
        </span>
      )}

      {participant.unstable && !hosting && (
        <span className="block w-full truncate text-center"
              style={{ fontSize: 9, color: 'var(--color-voice-weak)' }}>
          instável
        </span>
      )}
    </div>
  );
}

function MicBadgeIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 2.6a1.9 1.9 0 011.9 1.9v3.3a1.9 1.9 0 01-3.8 0V4.5A1.9 1.9 0 018 2.6z"
              stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M4.4 7.6a3.6 3.6 0 007.2 0" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" />
        <path d="M2.8 2.8l10.4 10.4" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2.6a1.9 1.9 0 011.9 1.9v3.3a1.9 1.9 0 01-3.8 0V4.5A1.9 1.9 0 018 2.6z"
            fill="currentColor" />
      <path d="M4.4 7.4a3.6 3.6 0 007.2 0M8 11.2v2.2" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ==============================================================================
   Superfície de vídeo
============================================================================== */

function VideoSurface({
  video,
  objectFit,
}: {
  video: StageVideo;
  objectFit: 'contain' | 'cover';
}) {
  const ref = useRef<HTMLVideoElement>(null);

  /*
   * `srcObject` é PROPRIEDADE, não atributo.
   *
   * React não a serializa: `<video src={stream}>` não funciona, e `src` com um
   * MediaStream produz a string "[object MediaStream]". Atribuir pelo ref é o
   * único caminho, e é por isso que existe este componente em vez de um
   * `<video>` inline.
   */
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.srcObject !== video.stream) {
      element.srcObject = video.stream;
    }
    /*
     * `play()` pode ser rejeitado, e a rejeição precisa ser engolida.
     *
     * A política de autoplay do navegador rejeita `play()` fora de gesto do
     * usuário. Com `muted` no vídeo local e áudio em elementos separados, o play
     * costuma passar — mas a rejeição é uma promise, e uma promise rejeitada sem
     * `catch` aparece no console como erro não tratado a cada troca de foco.
     */
    void element.play().catch(() => undefined);
  }, [video.stream]);

  return (
    <video
      ref={ref}
      /*
       * `muted` SEMPRE, inclusive em vídeo remoto.
       *
       * O áudio remoto vem por elementos `<audio>` dedicados, fora do palco — o
       * que permite volume por participante e sobrevive à troca de foco. Sem
       * `muted` aqui, o áudio tocaria DUAS vezes: uma pelo vídeo e uma pelo
       * elemento de áudio, produzindo eco e flanging.
       */
      muted
      playsInline
      autoPlay
      /* ----------------------------------------------------------------------
         Sem picture-in-picture
         ----------------------------------------------------------------------
         Aquele botão flutuante não é nosso: o Chrome o sobrepõe sozinho em
         qualquer `<video>` acima de um certo tamanho, mesmo sem `controls`. Não
         havia código para remover — havia um atributo faltando.

         Ele sai porque é uma armadilha aqui. A janela de PiP é UM vídeo, e uma
         chamada tem várias fontes; abrir uma delas em PiP tira a pessoa do
         palco, que é onde as outras estão, e o caminho de volta não é óbvio. A
         resposta certa para "quero isso maior" é a tela cheia, que o palco já
         faz com duplo clique e mantém a fileira de participantes visível.

         `disablePictureInPicture` é o atributo padrão para isso; o Safari usa
         `x-webkit-airplay`/`webkit-playsinline` para outras coisas e respeita
         este também.
      ---------------------------------------------------------------------- */
      disablePictureInPicture
      className="h-full w-full"
      style={{
        objectFit,
        /*
         * Espelha o vídeo LOCAL, e só ele.
         *
         * Câmera local espelhada é a convenção — a pessoa se vê como num
         * espelho. Espelhar vídeo remoto inverteria texto na tela
         * compartilhada, que é o conteúdo mais comum aqui.
         */
        transform: video.isLocal && video.kind === 'camera' ? 'scaleX(-1)' : undefined,
      }}
    />
  );
}

/* ==============================================================================
   Aviso do governador de banda
   ==============================================================================
   Fica no topo do palco e não num toast de canto: quando o governador pausa uma
   tela, a pessoa está OLHANDO o palco, e um aviso no canto oposto passa
   desapercebido justamente na hora em que ele importa.
============================================================================== */

function BandwidthNotice({
  notice,
  onDismiss,
}: {
  notice: string;
  onDismiss?: (() => void) | undefined;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex shrink-0 items-start px-3 py-2"
      style={{
        gap: 'var(--spacing-3)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-warning-subtle)',
        boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.08)',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"
           className="mt-0.5 shrink-0" style={{ color: 'var(--color-warning)' }}>
        <path d="M8 2.5l5.5 10h-11L8 2.5z" stroke="currentColor" strokeWidth="1.4"
              strokeLinejoin="round" />
        <path d="M8 6.5v3M8 11.2v.6" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" />
      </svg>

      <p className="min-w-0 flex-1"
         style={{
           fontSize: 'var(--text-xs)',
           lineHeight: 'var(--leading-normal)',
           color: 'var(--color-text-secondary)',
         }}>
        {notice}
      </p>

      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Fechar aviso"
                className="shrink-0"
                style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
          ✕
        </button>
      )}
    </div>
  );
}
